import { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Folder, FileText, ChevronDown, PanelLeft, Search } from 'lucide-react';

interface DocNode {
  id: string;
  type: 'folder' | 'document';
  title: string;
  slug: string;
  fullPath: string;
  icon?: string;
  children?: DocNode[];
  snippet?: string;
}

function TreeNode({
  node,
  depth = 0,
  prefix,
}: {
  node: DocNode;
  depth?: number;
  prefix: string;
}) {
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const isFolder = node.type === 'folder';
  const isActive = location.pathname === `${prefix}/docs/${node.fullPath}`;
  const pad = { paddingLeft: `${depth * 16 + 8}px` };

  if (isFolder) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 w-full text-left px-2 py-1.5 text-sm font-medium text-foreground hover:bg-surface-2/60 rounded transition-colors"
          style={pad}
        >
          <ChevronDown
            className={`h-3 w-3 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
          />
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{node.title}</span>
        </button>
        {open &&
          node.children?.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} prefix={prefix} />
          ))}
      </div>
    );
  }

  return (
    <Link
      to={`${prefix}/docs/${node.fullPath}`}
      className={`flex items-center gap-1.5 w-full px-2 py-1.5 text-sm rounded transition-colors ${
        isActive
          ? 'bg-brand-50 text-brand-700 font-medium dark:bg-brand-900/20 dark:text-brand-300'
          : 'text-muted-foreground hover:bg-surface-1/60 hover:text-foreground'
      }`}
      style={pad}
    >
      <span className="w-3 shrink-0" />
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span>{node.title}</span>
    </Link>
  );
}

export default function DocsLayout() {
  const { t } = useTranslation('docs');
  const { locale } = useParams<{ locale: string }>();
  const prefix = `/${locale}`;
  const [tree, setTree] = useState<DocNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocNode[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setSearchResults([]); setSearching(false); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get<{ data: DocNode[] }>(`/api/docs/search?q=${encodeURIComponent(q)}&limit=10&locale=${locale}`);
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, locale]);

  const fetchTree = useCallback(async () => {
    try {
      // 树标题按路由 locale 请求翻译覆盖（无参数时后端按 Accept-Language 兜底，切语言不生效）
      const res = await api.get<{ data: DocNode[] }>(`/api/docs/tree?locale=${locale}`);
      setTree(res.data);
    } catch {
      /* 树获取失败静默处理，侧边栏显示空态 */
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <div className="flex relative">
        {/* 侧边栏：固定定位，可收起 */}
        <aside
          className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 shrink-0 border-r border-border bg-surface-1/60 backdrop-blur-sm transition-transform duration-300 ease-out z-30 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="w-64 h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-foreground">{t('sidebarTitle')}</h2>
              <div className="relative mt-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-border bg-surface-0 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition"
                />
                {searchQuery.trim() && (
                  <div className="absolute z-40 mt-1 w-full max-h-80 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
                    {searching ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">...</p>
                    ) : searchResults.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">{t('searchNoResults')}</p>
                    ) : (
                      searchResults.map((r) => (
                        <Link
                          key={r.id}
                          to={`${prefix}/docs/${r.fullPath}`}
                          onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                          className="block px-3 py-2 hover:bg-surface-2 transition-colors"
                        >
                          <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                          {r.snippet && <p className="text-xs text-muted-foreground truncate">{r.snippet}</p>}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <nav className="p-2 space-y-0.5">
              {loading ? (
                <div className="space-y-2 p-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-6 bg-surface-2 rounded animate-pulse" />
                  ))}
                </div>
              ) : tree.length === 0 ? (
                <p className="text-sm text-muted-foreground px-3 py-2">{t('noDocuments')}</p>
              ) : (
                tree.map((node) => <TreeNode key={node.id} node={node} prefix={prefix} />)
              )}
            </nav>
          </div>
        </aside>

        {/* 内容区 */}
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ease-out ${
            sidebarOpen ? 'ml-64' : 'ml-0'
          }`}
        >
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`fixed top-20 z-40 p-1.5 bg-card/80 backdrop-blur-sm border border-border rounded-md shadow-sm text-muted-foreground hover:text-foreground transition-all duration-300 ${
              sidebarOpen ? 'left-[268px]' : 'left-3'
            }`}
            title={sidebarOpen ? t('hideSidebar') : t('showSidebar')}
          >
            <PanelLeft className="h-4 w-4" />
          </button>
          <div className="max-w-4xl mx-auto px-6 py-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
